import { z } from 'zod';
// Enums
export var LeadStatus;
(function (LeadStatus) {
    LeadStatus["NEW"] = "NEW";
    LeadStatus["CONTACTED"] = "CONTACTED";
    LeadStatus["QUALIFIED"] = "QUALIFIED";
    LeadStatus["PROPOSAL_SENT"] = "PROPOSAL_SENT";
    LeadStatus["NEGOTIATION"] = "NEGOTIATION";
    LeadStatus["APPOINTMENT_SCHEDULED"] = "APPOINTMENT_SCHEDULED";
    LeadStatus["ADMISSION_CONFIRMED"] = "ADMISSION_CONFIRMED";
    LeadStatus["CLOSED_WON"] = "CLOSED_WON";
    LeadStatus["CLOSED_LOST"] = "CLOSED_LOST";
    LeadStatus["ON_HOLD"] = "ON_HOLD";
})(LeadStatus || (LeadStatus = {}));
export var LeadSource;
(function (LeadSource) {
    LeadSource["WEBSITE"] = "WEBSITE";
    LeadSource["SOCIAL_MEDIA"] = "SOCIAL_MEDIA";
    LeadSource["REFERRAL"] = "REFERRAL";
    LeadSource["ADVERTISEMENT"] = "ADVERTISEMENT";
    LeadSource["WALK_IN"] = "WALK_IN";
    LeadSource["PHONE_CALL"] = "PHONE_CALL";
    LeadSource["EMAIL"] = "EMAIL";
    LeadSource["EVENT"] = "EVENT";
    LeadSource["PARTNER"] = "PARTNER";
    LeadSource["OTHER"] = "OTHER";
})(LeadSource || (LeadSource = {}));
export var LeadPriority;
(function (LeadPriority) {
    LeadPriority["LOW"] = "LOW";
    LeadPriority["MEDIUM"] = "MEDIUM";
    LeadPriority["HIGH"] = "HIGH";
    LeadPriority["HOT"] = "HOT";
})(LeadPriority || (LeadPriority = {}));
// Lead Stage Configuration
export const LeadStageSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(100),
    description: z.string().optional(),
    color: z.string().regex(/^#[0-9A-F]{6}$/i), // Hex color
    order: z.number().min(0),
    isDefault: z.boolean().default(false),
    isActive: z.boolean().default(true),
    tenantId: z.string().uuid(),
    // Stage behavior
    requiresApproval: z.boolean().default(false),
    autoCreateTask: z.boolean().default(false),
    taskTemplate: z.object({
        title: z.string(),
        description: z.string(),
        type: z.string(),
        priority: z.string(),
        dueInDays: z.number(),
    }).optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
// Lead Attribute Configuration
export const LeadAttributeSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(100),
    label: z.string().min(1).max(100),
    type: z.enum(['TEXT', 'NUMBER', 'EMAIL', 'PHONE', 'DATE', 'BOOLEAN', 'SELECT', 'MULTISELECT', 'TEXTAREA']),
    required: z.boolean().default(false),
    order: z.number().min(0),
    // For SELECT and MULTISELECT types
    options: z.array(z.object({
        value: z.string(),
        label: z.string(),
    })).optional(),
    // Validation rules
    validation: z.object({
        minLength: z.number().optional(),
        maxLength: z.number().optional(),
        min: z.number().optional(),
        max: z.number().optional(),
        pattern: z.string().optional(),
    }).optional(),
    tenantId: z.string().uuid(),
    isActive: z.boolean().default(true),
    createdAt: z.date(),
    updatedAt: z.date(),
});
// Main Lead Schema
export const LeadSchema = z.object({
    id: z.string().uuid(),
    // Basic Information
    firstName: z.string().min(1).max(100),
    lastName: z.string().min(1).max(100),
    email: z.string().email(),
    phone: z.string(),
    alternatePhone: z.string().optional(),
    // Lead Details
    source: z.nativeEnum(LeadSource),
    status: z.string(), // Dynamic based on organization's stages
    priority: z.nativeEnum(LeadPriority).default(LeadPriority.MEDIUM),
    // Organization and Assignment
    tenantId: z.string().uuid(),
    schoolId: z.string().uuid().optional(),
    assignedTo: z.string().uuid().optional(),
    assignedBy: z.string().uuid().optional(),
    assignedAt: z.date().optional(),
    // Student Information (for school leads)
    studentInfo: z.object({
        name: z.string(),
        dateOfBirth: z.date().optional(),
        gender: z.enum(['MALE', 'FEMALE', 'OTHER']).optional(),
        grade: z.string().optional(),
        previousSchool: z.string().optional(),
        interests: z.array(z.string()).default([]),
    }).optional(),
    // Parent/Guardian Information
    parentInfo: z.object({
        fatherName: z.string().optional(),
        motherName: z.string().optional(),
        guardianName: z.string().optional(),
        occupation: z.string().optional(),
        annualIncome: z.number().optional(),
        address: z.object({
            street: z.string(),
            city: z.string(),
            state: z.string(),
            country: z.string(),
            zipCode: z.string(),
        }).optional(),
    }).optional(),
    // Lead Tracking
    expectedValue: z.number().optional(),
    probability: z.number().min(0).max(100).default(0),
    expectedCloseDate: z.date().optional(),
    // Custom Attributes (dynamic based on organization configuration)
    customAttributes: z.record(z.string(), z.any()).default({}),
    // Communication History
    lastContactDate: z.date().optional(),
    nextFollowUpDate: z.date().optional(),
    // Metadata
    tags: z.array(z.string()).default([]),
    notes: z.string().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const CreateLeadSchema = LeadSchema.omit({
    id: true,
    assignedAt: true,
    createdAt: true,
    updatedAt: true,
});
export const UpdateLeadSchema = CreateLeadSchema.partial();
export const BulkLeadUploadSchema = z.object({
    leads: z.array(z.object({
        firstName: z.string(),
        lastName: z.string(),
        email: z.string().email(),
        phone: z.string(),
        source: z.nativeEnum(LeadSource),
        grade: z.string().optional(),
        parentName: z.string().optional(),
        address: z.string().optional(),
        notes: z.string().optional(),
    })),
    assignTo: z.string().uuid().optional(),
});
export const LeadAssignmentSchema = z.object({
    leadIds: z.array(z.string().uuid()),
    assignTo: z.string().uuid(),
    assignmentType: z.enum(['INDIVIDUAL', 'ROUND_ROBIN', 'LOAD_BALANCE']),
});
export const LeadFilterSchema = z.object({
    status: z.array(z.string()).optional(),
    source: z.array(z.nativeEnum(LeadSource)).optional(),
    priority: z.array(z.nativeEnum(LeadPriority)).optional(),
    assignedTo: z.array(z.string().uuid()).optional(),
    schoolId: z.string().uuid().optional(),
    createdDateFrom: z.date().optional(),
    createdDateTo: z.date().optional(),
    expectedCloseDateFrom: z.date().optional(),
    expectedCloseDateTo: z.date().optional(),
    tags: z.array(z.string()).optional(),
    search: z.string().optional(),
    customAttributes: z.record(z.string(), z.any()).optional(),
});
// Default Lead Stages
export const DEFAULT_LEAD_STAGES = [
    {
        name: 'New Lead',
        description: 'Newly created lead, not yet contacted',
        color: '#3B82F6',
        order: 1,
        isDefault: true,
        isActive: true,
        requiresApproval: false,
        autoCreateTask: true,
        taskTemplate: {
            title: 'Contact New Lead',
            description: 'Make initial contact with the lead',
            type: 'LEAD_FOLLOWUP',
            priority: 'HIGH',
            dueInDays: 1,
        },
    },
    {
        name: 'Contacted',
        description: 'Initial contact made with the lead',
        color: '#10B981',
        order: 2,
        isDefault: false,
        isActive: true,
        requiresApproval: false,
        autoCreateTask: false,
    },
    {
        name: 'Qualified',
        description: 'Lead has been qualified as a potential student',
        color: '#8B5CF6',
        order: 3,
        isDefault: false,
        isActive: true,
        requiresApproval: false,
        autoCreateTask: true,
        taskTemplate: {
            title: 'Send Admission Information',
            description: 'Send detailed admission information and application form',
            type: 'ADMISSION_PROCESS',
            priority: 'HIGH',
            dueInDays: 2,
        },
    },
    {
        name: 'Application Submitted',
        description: 'Admission application has been submitted',
        color: '#F59E0B',
        order: 4,
        isDefault: false,
        isActive: true,
        requiresApproval: false,
        autoCreateTask: true,
        taskTemplate: {
            title: 'Review Application',
            description: 'Review and process the admission application',
            type: 'ADMISSION_PROCESS',
            priority: 'HIGH',
            dueInDays: 3,
        },
    },
    {
        name: 'Interview Scheduled',
        description: 'Interview or assessment scheduled',
        color: '#06B6D4',
        order: 5,
        isDefault: false,
        isActive: true,
        requiresApproval: false,
        autoCreateTask: false,
    },
    {
        name: 'Admission Offered',
        description: 'Admission offer has been made',
        color: '#84CC16',
        order: 6,
        isDefault: false,
        isActive: true,
        requiresApproval: true,
        autoCreateTask: true,
        taskTemplate: {
            title: 'Follow up on Admission Offer',
            description: 'Follow up with family regarding admission offer',
            type: 'LEAD_FOLLOWUP',
            priority: 'HIGH',
            dueInDays: 7,
        },
    },
    {
        name: 'Enrolled',
        description: 'Student has been successfully enrolled',
        color: '#22C55E',
        order: 7,
        isDefault: false,
        isActive: true,
        requiresApproval: false,
        autoCreateTask: false,
    },
    {
        name: 'Lost',
        description: 'Lead did not convert to enrollment',
        color: '#EF4444',
        order: 8,
        isDefault: false,
        isActive: true,
        requiresApproval: false,
        autoCreateTask: false,
    },
];
// Default Lead Attributes
export const DEFAULT_LEAD_ATTRIBUTES = [
    {
        name: 'preferredStartDate',
        label: 'Preferred Start Date',
        type: 'DATE',
        required: false,
        order: 1,
        isActive: true,
    },
    {
        name: 'budget',
        label: 'Budget Range',
        type: 'SELECT',
        required: false,
        order: 2,
        options: [
            { value: 'under_50k', label: 'Under ₹50,000' },
            { value: '50k_100k', label: '₹50,000 - ₹1,00,000' },
            { value: '100k_200k', label: '₹1,00,000 - ₹2,00,000' },
            { value: 'above_200k', label: 'Above ₹2,00,000' },
        ],
        isActive: true,
    },
    {
        name: 'transportRequired',
        label: 'Transport Required',
        type: 'BOOLEAN',
        required: false,
        order: 3,
        isActive: true,
    },
    {
        name: 'specialNeeds',
        label: 'Special Educational Needs',
        type: 'TEXTAREA',
        required: false,
        order: 4,
        isActive: true,
    },
    {
        name: 'referredBy',
        label: 'Referred By',
        type: 'TEXT',
        required: false,
        order: 5,
        isActive: true,
    },
];
// Lead Factory
export class LeadFactory {
    static createLead(data) {
        const lead = {
            id: crypto.randomUUID(),
            createdAt: new Date(),
            updatedAt: new Date(),
            ...data,
        };
        return LeadSchema.parse(lead);
    }
    static createLeadStage(tenantId, data) {
        return LeadStageSchema.parse({
            id: crypto.randomUUID(),
            tenantId,
            createdAt: new Date(),
            updatedAt: new Date(),
            ...data,
        });
    }
    static createLeadAttribute(tenantId, data) {
        return LeadAttributeSchema.parse({
            id: crypto.randomUUID(),
            tenantId,
            createdAt: new Date(),
            updatedAt: new Date(),
            ...data,
        });
    }
}
// Lead Permissions
export class LeadPermissions {
    static canViewLeads(userRole) {
        return [
            'SUPER_ADMIN',
            'ORG_ADMIN',
            'PRINCIPAL',
            'SALES_MANAGER',
            'SALES_REP',
        ].includes(userRole);
    }
    static canCreateLead(userRole) {
        return [
            'SUPER_ADMIN',
            'ORG_ADMIN',
            'PRINCIPAL',
            'SALES_MANAGER',
            'SALES_REP',
        ].includes(userRole);
    }
    static canEditLead(userRole, userId, lead) {
        // Super admin and org admin can edit all leads
        if (['SUPER_ADMIN', 'ORG_ADMIN'].includes(userRole)) {
            return true;
        }
        // Principals can edit all leads in their organization
        if (userRole === 'PRINCIPAL') {
            return true;
        }
        // Sales managers can edit all leads
        if (userRole === 'SALES_MANAGER') {
            return true;
        }
        // Sales representatives can edit leads assigned to them
        if (userRole === 'SALES_REP' && lead.assignedTo === userId) {
            return true;
        }
        return false;
    }
    static canAssignLeads(userRole) {
        return [
            'SUPER_ADMIN',
            'ORG_ADMIN',
            'PRINCIPAL',
            'SALES_MANAGER',
        ].includes(userRole);
    }
    static canConfigureLeadStages(userRole) {
        return [
            'SUPER_ADMIN',
            'ORG_ADMIN',
            'SALES_MANAGER',
        ].includes(userRole);
    }
    static canConfigureLeadAttributes(userRole) {
        return [
            'SUPER_ADMIN',
            'ORG_ADMIN',
            'SALES_MANAGER',
        ].includes(userRole);
    }
    static canUploadBulkLeads(userRole) {
        return [
            'SUPER_ADMIN',
            'ORG_ADMIN',
            'PRINCIPAL',
            'SALES_MANAGER',
        ].includes(userRole);
    }
    static canDeleteLead(userRole) {
        return [
            'SUPER_ADMIN',
            'ORG_ADMIN',
            'SALES_MANAGER',
        ].includes(userRole);
    }
}
// Lead Status Transitions
export class LeadStatusTransitions {
    static canTransition(fromStatus, toStatus, stages) {
        const fromStage = stages.find(s => s.name === fromStatus);
        const toStage = stages.find(s => s.name === toStatus);
        if (!fromStage || !toStage)
            return false;
        // Allow moving to any active stage (business logic can be customized)
        return toStage.isActive;
    }
    static requiresApproval(toStatus, stages) {
        const stage = stages.find(s => s.name === toStatus);
        return stage?.requiresApproval || false;
    }
    static shouldCreateTask(toStatus, stages) {
        const stage = stages.find(s => s.name === toStatus);
        return stage?.autoCreateTask ? stage.taskTemplate || null : null;
    }
}
//# sourceMappingURL=Lead.js.map